import { useEffect, useState } from "react";
import api from "../api/client";
import EditableSelect from "../components/EditableSelect";
import { useExpenseOptions } from "../context/ExpenseOptionsContext";

const MONTHS = Array.from({ length: 12 }, (_, i) => {
  const d = new Date(); d.setMonth(d.getMonth() - i);
  return d.toISOString().slice(0, 7);
});

const CSS = `
.bg-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:24px; flex-wrap:wrap; gap:12px; }
.bg-title  { font-size:18px; font-weight:700; color:#0d1a36; }
.bg-header-right { display:flex; gap:10px; align-items:center; }
.bg-select { background:#f4f8ff; border:1px solid #c0d0eb; border-radius:7px; padding:8px 12px;
  font-size:13px; color:#0d1a36; outline:none; font-family:inherit; cursor:pointer; }
.bg-select:focus { border-color:#059669; }
.bg-btn { display:inline-flex; align-items:center; gap:6px; padding:8px 16px; border-radius:7px;
  border:none; cursor:pointer; font-size:13px; font-weight:600; transition:opacity .15s; font-family:inherit; }
.bg-btn:disabled { opacity:.4; cursor:not-allowed; }
.bg-btn:not(:disabled):hover { opacity:.88; }
.bg-btn-primary { background:#059669; color:#f0fdf4; }
.bg-btn-success { background:#2563eb; color:#fff; }
.bg-btn-ghost   { background:none; border:1px solid #c0d0eb; color:#2a4a78; }
.bg-btn-sm      { padding:5px 12px; font-size:12px; }

.bg-alert { padding:10px 16px; border-radius:8px; font-size:13px; margin-bottom:16px; }
.bg-alert-success { background:rgba(16,185,129,.08); border:1px solid rgba(16,185,129,.2); color:#059669; }
.bg-alert-error   { background:rgba(248,113,113,.08); border:1px solid rgba(248,113,113,.2); color:#dc2626; }

.bg-add-card { background:#ffffff; border:1px solid #c0d0eb; border-radius:10px; padding:20px; margin-bottom:20px; }
.bg-add-title { font-size:11px; font-weight:700; letter-spacing:.07em; text-transform:uppercase; color:#6080aa; margin-bottom:14px; }
.bg-add-row  { display:flex; gap:10px; align-items:flex-end; flex-wrap:wrap; }
.bg-field    { display:flex; flex-direction:column; gap:5px; flex:1; min-width:140px; }
.bg-label    { font-size:11px; font-weight:600; letter-spacing:.06em; text-transform:uppercase; color:#6080aa; }
.bg-input    { background:#f4f8ff; border:1px solid #d5e3f5; border-radius:7px; padding:9px 12px;
  font-size:13px; color:#0d1a36; outline:none; font-family:inherit; transition:border-color .15s; }
.bg-input:focus { border-color:#059669; }

.bg-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(240px,1fr)); gap:14px; }
.bg-card { background:#ffffff; border-radius:10px; padding:20px; transition:border-color .2s; }
.bg-card-normal { border:1px solid #c0d0eb; }
.bg-card-over   { border:1px solid rgba(248,113,113,.4); }
.bg-card-header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:12px; }
.bg-cat-name    { font-size:14px; font-weight:600; color:#0d1a36; }
.bg-card-actions{ display:flex; gap:10px; align-items:center; }
.bg-edit-btn    { background:none; border:none; color:#2563eb; cursor:pointer; font-size:14px; opacity:.7; padding:0; line-height:1; }
.bg-edit-btn:hover { opacity:1; }
.bg-del-btn     { background:none; border:none; color:#dc2626; cursor:pointer; font-size:16px; opacity:.6; padding:0; line-height:1; }
.bg-del-btn:hover { opacity:1; }
.bg-edit-row    { display:flex; gap:8px; align-items:center; margin-bottom:8px; flex-wrap:wrap; }
.bg-edit-row .bg-input { padding:7px 10px; font-size:12px; }
.bg-edit-currency { width:70px; }
.bg-edit-amount { width:90px; }
.bg-amounts     { display:flex; justify-content:space-between; font-size:12px; color:#6080aa; margin-bottom:8px; }
.bg-amounts span { color:#2a4a78; font-variant-numeric:tabular-nums; }
.bg-track { height:8px; background:#f4f8ff; border-radius:4px; overflow:hidden; margin-bottom:6px; }
.bg-fill  { height:100%; border-radius:4px; transition:width .4s; }
.bg-status { font-size:11px; font-weight:600; font-variant-numeric:tabular-nums; }
.bg-status-ok   { color:#059669; }
.bg-status-over { color:#dc2626; }
.bg-empty { font-size:13px; color:#6080aa; padding:20px 0; }
.bg-spinner-wrap { display:flex; justify-content:center; align-items:center; height:200px; }
.bg-spinner { width:32px; height:32px; border:3px solid #c0d0eb; border-top-color:#059669; border-radius:50%; animation:bgspin .6s linear infinite; }
@keyframes bgspin { to{ transform:rotate(360deg); } }
`;

export default function Budget() {
  const { categories, currencies, addOption } = useExpenseOptions();
  const [month,    setMonth]   = useState(new Date().toISOString().slice(0, 7));
  const [budgets,  setBudgets] = useState([]);
  const [summary,  setSummary] = useState([]);
  const [loading,  setLoading] = useState(true);
  const [adding,   setAdding]  = useState(false);
  const [form,     setForm]    = useState({ category: "", amount: "", currency: "OMR" });
  const [editing,  setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ amount: "", currency: "OMR" });
  const [alert,    setAlert]   = useState({ msg:"", type:"" });

  useEffect(() => {
    if (categories.length && !form.category) setForm(f => ({ ...f, category: categories[0] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories]);

  const notify = (msg, type="success") => {
    setAlert({ msg, type });
    setTimeout(() => setAlert({ msg:"", type:"" }), 3000);
  };

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/expense/budgets?month=${month}`),
      api.get(`/expense/summary?month=${month}`),
    ]).then(([b, s]) => { setBudgets(b.data); setSummary(s.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [month]);

  const addBudget = async () => {
    if (!form.category || !form.amount) return;
    try {
      await api.post("/expense/budgets", { ...form, month, amount: parseFloat(form.amount) });
      setAdding(false); setForm({ category: categories[0] || "", amount: "", currency: "OMR" });
      load(); notify("Budget added!");
    } catch (e) { notify(e.response?.data?.detail || "Failed", "error"); }
  };

  const deleteBudget = async (category) => {
    await api.delete(`/expense/budgets/${month}/${category}`);
    load();
  };

  const startEdit = (b) => {
    setEditing(b.category);
    setEditForm({ amount: b.amount, currency: b.currency || "OMR" });
  };

  const saveEdit = async (category) => {
    if (!editForm.amount) return;
    try {
      await api.post("/expense/budgets", { month, category, amount: parseFloat(editForm.amount), currency: editForm.currency });
      setEditing(null);
      load(); notify("Budget updated!");
    } catch (e) { notify(e.response?.data?.detail || "Failed", "error"); }
  };

  const spendMap = {};
  summary.forEach(s => {
    const key = `${s.category}|${s.currency||"OMR"}`;
    spendMap[key] = (spendMap[key] || 0) + parseFloat(s.total);
  });

  if (loading) return <><style>{CSS}</style><div className="bg-spinner-wrap"><div className="bg-spinner"/></div></>;

  return (
    <>
      <style>{CSS}</style>
      <div className="bg-header">
        <div className="bg-title">💼 Budget</div>
        <div className="bg-header-right">
          <select className="bg-select" value={month} onChange={e=>setMonth(e.target.value)}>
            {MONTHS.map(m=><option key={m} value={m}>{m}</option>)}
          </select>
          <button className="bg-btn bg-btn-primary" onClick={()=>setAdding(a=>!a)}>
            {adding ? "✕ Cancel" : "+ Add Budget"}
          </button>
        </div>
      </div>

      {alert.msg && <div className={`bg-alert bg-alert-${alert.type}`}>{alert.msg}</div>}

      {adding && (
        <div className="bg-add-card">
          <div className="bg-add-title">New Budget Entry</div>
          <div className="bg-add-row">
            <div className="bg-field">
              <label className="bg-label">Category</label>
              <EditableSelect className="bg-input" value={form.category} options={categories}
                              onChange={v=>setForm(f=>({...f,category:v}))}
                              onAddNew={v=>addOption("category", v)} />
            </div>
            <div className="bg-field" style={{maxWidth:110}}>
              <label className="bg-label">Currency</label>
              <EditableSelect className="bg-input" value={form.currency} options={currencies}
                              onChange={v=>setForm(f=>({...f,currency:v}))}
                              onAddNew={v=>addOption("currency", v)} />
            </div>
            <div className="bg-field" style={{maxWidth:160}}>
              <label className="bg-label">Amount</label>
              <input className="bg-input" type="number" min="0" step="0.001"
                     value={form.amount} onChange={e=>setForm(f=>({...f,amount:e.target.value}))}
                     placeholder="0.000" />
            </div>
            <button className="bg-btn bg-btn-success" onClick={addBudget} disabled={!form.amount}>Save</button>
            <button className="bg-btn bg-btn-ghost" onClick={()=>setAdding(false)}>Cancel</button>
          </div>
        </div>
      )}

      {budgets.length === 0
        ? <div className="bg-empty">No budgets for {month}. Click "Add Budget" to set one.</div>
        : (
          <div className="bg-grid">
            {budgets.map(b => {
              const currency = b.currency || "OMR";
              const spent    = spendMap[`${b.category}|${currency}`] || 0;
              const budget   = parseFloat(b.amount);
              const pct      = budget > 0 ? Math.min((spent / budget) * 100, 100) : 0;
              const over     = spent > budget;
              const isEditing = editing === b.category;
              return (
                <div key={b.category} className={`bg-card ${over?"bg-card-over":"bg-card-normal"}`}>
                  <div className="bg-card-header">
                    <div className="bg-cat-name">{b.category}</div>
                    <div className="bg-card-actions">
                      <button className="bg-edit-btn" onClick={()=>isEditing?setEditing(null):startEdit(b)}>✎</button>
                      <button className="bg-del-btn" onClick={()=>deleteBudget(b.category)}>×</button>
                    </div>
                  </div>

                  {isEditing ? (
                    <div className="bg-edit-row">
                      <EditableSelect className="bg-input bg-edit-currency" value={editForm.currency} options={currencies}
                                      onChange={v=>setEditForm(f=>({...f,currency:v}))}
                                      onAddNew={v=>addOption("currency", v)} />
                      <input className="bg-input bg-edit-amount" type="number" min="0" step="0.001"
                             value={editForm.amount} onChange={e=>setEditForm(f=>({...f,amount:e.target.value}))} />
                      <button className="bg-btn bg-btn-success bg-btn-sm" onClick={()=>saveEdit(b.category)} disabled={!editForm.amount}>Save</button>
                      <button className="bg-btn bg-btn-ghost bg-btn-sm" onClick={()=>setEditing(null)}>Cancel</button>
                    </div>
                  ) : (
                    <>
                      <div className="bg-amounts">
                        Spent: <span style={{color:over?"#dc2626":"#0d1a36"}}>{currency} {spent.toFixed(3)}</span>
                        &nbsp;/&nbsp;{currency} {budget.toFixed(3)}
                      </div>
                      <div className="bg-track">
                        <div className="bg-fill" style={{width:`${pct}%`,background:over?"#dc2626":"#059669"}} />
                      </div>
                      <div className={`bg-status ${over?"bg-status-over":"bg-status-ok"}`}>
                        {over
                          ? `Over by ${currency} ${(spent-budget).toFixed(3)}`
                          : `${currency} ${(budget-spent).toFixed(3)} remaining`}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )
      }
    </>
  );
}
